import React, { useEffect, useState } from "react";
import { formatIDR } from "@/lib/api";
import { Modal } from "@/components/erp/ui";

export const METRICS = [["perf", "Nilai"], ["late", "Telat masuk"], ["ot", "Lembur"], ["wait", "Tahap WO telat"], ["break", "Istirahat"], ["penalty", "Potongan"], ["bonus", "Bonus disiplin"]];
const hm = (m) => `${Math.floor((m || 0) / 60)} j ${(m || 0) % 60} mnt`;
const num = (x) => Math.round((x || 0) * 10) / 10;

const Line = ({ label, value, tone = "", sub }) => (
  <div className="flex justify-between gap-3 py-1.5 border-b border-slate-100 text-sm">
    <span>{label}{sub && <div className="text-[11px] text-slate-400">{sub}</div>}</span>
    <b className={`whitespace-nowrap ${tone}`}>{value}</b>
  </div>
);
const Dates = ({ rows, empty = "Tidak ada kejadian bulan ini." }) => rows.length ? (
  <div className="max-h-48 overflow-y-auto text-xs mt-1">{rows.map((r, i) => <div key={i} className="flex justify-between py-1 border-b border-slate-50"><span>{r[0]}</span><span className="text-slate-600">{r[1]}</span></div>)}</div>
) : <div className="text-xs text-slate-400 mt-1">{empty}</div>;

const Perf = ({ e }) => {
  const p = e.pts_rule, R = e.rates;
  const minus = (n, pt, label, sub) => n ? <Line label={`${label} (${n}× · ${pt} poin)`} sub={sub} value={`−${num(n * pt)}`} tone="text-rose-600" /> : null;
  return (<div>
    <Line label="Nilai awal bulan" value={R.perf_start} />
    {minus(e.late_days, p.late, "Telat masuk", (e.late_dates || []).map((d) => d[0]).join(", "))}
    {minus(Math.max(0, e.wait_late - (e.busy_late || 0)), p.wait, "Tahap WO telat", e.busy_late ? `${e.busy_late} tahap tidak dipotong karena masih mengerjakan WO lain` : "")}
    {minus(e.early_uncovered_days, p.early, "Pulang awal tidak diganti")}
    {minus(e.alfa_days, p.alfa, "Alfa / tanpa keterangan", (e.alfa_dates || []).join(", "))}
    {minus(e.sick_days, p.sick, "Sakit", (e.sick_dates || []).join(", "))}
    {(e.slow_picks || []).map((s, i) => <Line key={`s${i}`} label={`Lama diambil ${s.wait_min} mnt (batas 60): ${s.label}`} sub={s.date} value={`−${num(e.slow_rule)}`} tone="text-rose-600" />)}
    {e.calib_over > 0 && <Line label={`Kalibrasi lewat batas (${e.calib_over}×)`} value={`−${num(e.calib_pts)}`} tone="text-rose-600" />}
    {(e.office_list || []).map((o, i) => <Line key={`o${i}`} label={o.title || "Aktivitas kantor"} sub={o.date} value={`${o.points > 0 ? "−" : "+"}${num(Math.abs(o.points))}`} tone={o.points > 0 ? "text-rose-600" : "text-emerald-600"} />)}
    {(e.rb_lines || []).map((l, i) => <Line key={`r${i}`} label={l.label} sub={l.detail} value={`${l.points > 0 ? "+" : "−"}${num(Math.abs(l.points))}`} tone={l.points > 0 ? "text-emerald-600" : "text-rose-600"} />)}
    <Line label="Nilai akhir" value={e.perf} tone="text-slate-900" />
  </div>);
};

const Late = ({ e }) => (<div>
  <Line label="Total telat" value={`${e.late_days}× · ${e.late_min} mnt`} />
  {e.rates.offset && <Line label="Dikurangi lembur" value={`−${Math.max(0, e.late_min - e.net_late_min)} mnt`} tone="text-emerald-600" />}
  <Line label="Telat bersih" value={`${e.net_late_min} mnt`} />
  <Line label={`Potongan (${formatIDR(e.rates.late)}/mnt)`} value={formatIDR(e.att_penalty)} tone="text-rose-600" />
  <div className="text-xs font-semibold mt-3">Tanggal telat</div>
  <Dates rows={(e.late_dates || []).map((d) => [d[0], `${d[1]} mnt`])} />
  {(e.late_forgiven || []).length > 0 && <><div className="text-xs font-semibold mt-3">Telat dimaafkan</div><Dates rows={e.late_forgiven.map((d) => [d[0], `${d[1]} mnt`])} /></>}
</div>);

const Ot = ({ e }) => (<div>
  <Line label="Total lembur" value={`${e.overtime_min} mnt`} />
  <Line label="Dipakai menutup hutang jam (alfa)" value={hm(e.debt_repaid_min)} tone="text-amber-600" />
  <Line label="Dipakai menutup pulang awal" value={hm(e.early_repaid_min)} tone="text-amber-600" />
  <Line label="Lembur dibayar" value={`${e.ot_paid_min} mnt · ${formatIDR(e.ot_pay)}`} tone="text-emerald-600" />
  <Line label={`Uang makan lembur (${e.meal_days} hari)`} value={formatIDR(e.meal_pay)} />
  {e.hour_debt_min > 0 && <Line label="Sisa hutang jam" value={hm(e.debt_left_min)} tone="text-rose-600" />}
</div>);

const Wait = ({ e }) => (<div>
  <Line label="Tahap dikerjakan" value={e.stages} />
  <Line label="Tahap telat" value={`${e.wait_late} (${e.late_stage_pct}%)`} sub={`Batas ${e.rates.max_late_stage_pct}%`} />
  <Line label="Lewat batas waktu" value={`${e.wait_over_min} mnt`} />
  <Line label={`Potongan (${formatIDR(e.rates.wait)}/mnt)`} value={formatIDR(e.wait_penalty)} tone="text-rose-600" />
  <div className="text-xs font-semibold mt-3">Tahap yang telat</div>
  <Dates rows={(e.wait_dates || []).map((d) => [d[0], `${d[1]} · ${d[2]}`])} />
</div>);

const Brk = ({ e }) => (<div>
  <Line label="Porsi istirahat dari jam kerja" value={`${e.break_pct}%`} />
  <Line label="Batas maksimal" value={`${e.rates.max_break_pct}%`} />
  <div className="text-xs mt-2 text-slate-500">{e.break_pct > e.rates.max_break_pct ? "Melebihi batas: bonus bulan ini tidak didapat." : "Masih di bawah batas."}</div>
</div>);

const Pen = ({ e }) => (<div>
  <Line label={`Telat masuk: ${e.net_late_min} mnt × ${formatIDR(e.rates.late)}`} value={formatIDR(e.att_penalty)} tone="text-rose-600" />
  <Line label={`Tahap WO telat: ${e.wait_over_min} mnt × ${formatIDR(e.rates.wait)}`} value={formatIDR(e.wait_penalty)} tone="text-rose-600" />
  <Line label={`Pulang awal tidak diganti: ${e.early_left_min} mnt × ${formatIDR(e.rates.early)}`} value={formatIDR(e.early_penalty)} tone="text-rose-600" />
  <Line label="Total potongan" value={formatIDR(e.penalty)} tone="text-rose-700" />
  {(e.early_dates || []).length > 0 && <><div className="text-xs font-semibold mt-3">Tanggal pulang awal</div><Dates rows={e.early_dates.map((d) => [d[0], `${d[1]} mnt`])} /></>}
  {e.exempt && <div className="text-xs text-sky-700 mt-2">Role ini dikecualikan dari sebagian potongan.</div>}
</div>);

const Bonus = ({ e }) => (<div>
  <div className={`text-xs mb-2 ${e.bonus_eligible ? "text-emerald-700" : "text-rose-600"}`}>{e.bonus_eligible ? "Memenuhi syarat bonus." : `Tidak memenuhi syarat: ${e.bonus_notes.join("; ")}`}</div>
  <Line label="Bonus tanpa telat" sub={e.late_days || e.wait_late ? `Tidak dapat: ada ${e.late_days} telat masuk / ${e.wait_late} tahap telat` : `Nilai aturan ${formatIDR(e.rates.bonus_no_late)}`} value={formatIDR(e.bonus_no_late)} tone="text-emerald-600" />
  <Line label={`Bonus tahap tepat waktu (${e.ontime_bonus_stages} dari ${e.ontime_stages} tahap)`} sub={`${formatIDR(e.rates.bonus_ontime_stage)}/tahap${e.rates.bonus_ontime_stage_pct ? ` + ${e.rates.bonus_ontime_stage_pct}% harga WO` : ""}`} value={formatIDR(e.bonus_ontime)} tone="text-emerald-600" />
  <Line label="Total bonus disiplin" value={formatIDR(e.disc_bonus)} tone="text-emerald-700" />
</div>);

const VIEW = { perf: Perf, late: Late, ot: Ot, wait: Wait, break: Brk, penalty: Pen, bonus: Bonus };
const thisMonth = () => new Date(Date.now() + 7 * 3600e3).toISOString().slice(0, 7);

export const DiscDetail = ({ open, onClose, metric, setMetric, load, name, month0 }) => {
  const [month, setMonth] = useState(month0 || thisMonth());
  const [e, setE] = useState(null);
  useEffect(() => { if (open) { setMonth(month0 || thisMonth()); } }, [open, month0]);
  useEffect(() => { if (open && month) { setE(null); load(month).then(setE).catch(() => setE(false)); } }, [open, month, load]);
  const V = VIEW[metric] || Perf;
  return (
    <Modal open={open} onClose={onClose} title={`Rincian ${name || ""}`.trim()}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input type="month" data-testid="disc-detail-month" value={month} onChange={(x) => setMonth(x.target.value)} className="border border-slate-300 rounded-lg px-2 py-1 text-sm" />
      </div>
      <div className="flex flex-wrap gap-1 mb-3">{METRICS.map(([k, l]) => <button key={k} data-testid={`disc-detail-tab-${k}`} onClick={() => setMetric(k)} className={`px-2.5 py-1 rounded-full text-xs ${metric === k ? "bg-slate-900 text-white" : "border border-slate-300"}`}>{l}</button>)}</div>
      <div data-testid="disc-detail-body">{e === null ? <div className="text-sm text-slate-400">Memuat…</div> : e === false ? <div className="text-sm text-rose-600">Data tidak tersedia untuk bulan ini.</div> : <V e={e} />}</div>
    </Modal>
  );
};
